import { Table, Checkbox, useLocalization } from '@magicdoorinc/ui';
import { createEffect, createMemo, createSignal } from 'solid-js';
import { useBankAccountsList, useCompanyTypeConfirm } from '~/contexts/local';
import type { TableColumns } from '@magicdoorinc/ui';
import type { Component } from 'solid-js';
import type { HydratedBankAccountDto } from '~/swagger/portal/Api';

interface BankAccountChooseProps {
  propertyAccountIds?: string[];
  companyAccountIds?: string[];
  onPropertyAccountChange?: (accountIds: string[]) => void;
  onCompanyAccountChange?: (accountIds: string[]) => void;
}

type AccountType = 'property' | 'company';

const BankAccountChoose: Component<BankAccountChooseProps> = (props) => {
  const { t } = useLocalization();
  const { bankAccounts } = useBankAccountsList();
  const { unassignedBankAccounts } = useCompanyTypeConfirm();
  const [accountTypeMap, setAccountTypeMap] = createSignal<Map<string, AccountType>>(new Map());

  const getAccountRestriction = (accountId: string) => {
    const unassignedAccount = unassignedBankAccounts()?.find((acc) => acc.id === accountId);
    return {
      cannotBeCompanyAccount: unassignedAccount?.cannotBeCompanyAccount ?? false,
      reason: unassignedAccount?.cannotBeCompanyAccountReason,
    };
  };

  createEffect(() => {
    const map = new Map<string, AccountType>();
    props.propertyAccountIds?.forEach((id) => map.set(id, 'property'));
    props.companyAccountIds?.forEach((id) => map.set(id, 'company'));
    setAccountTypeMap(map);
  });

  const handleAccountTypeChange = (accountId: string, type: AccountType | undefined) => {
    const newMap = new Map(accountTypeMap());
    if (type) {
      newMap.set(accountId, type);
    } else {
      newMap.delete(accountId);
    }
    setAccountTypeMap(newMap);

    const propertyIds: string[] = [];
    const companyIds: string[] = [];
    newMap.forEach((value, key) => {
      if (value === 'property') {
        propertyIds.push(key);
      } else if (value === 'company') {
        companyIds.push(key);
      }
    });

    props.onPropertyAccountChange?.(propertyIds);
    props.onCompanyAccountChange?.(companyIds);
  };

  const columns = createMemo<TableColumns<HydratedBankAccountDto>>(() => [
    {
      title: t('Bank account'),
      render: (account) => {
        const restriction = getAccountRestriction(account.id);
        const isExistingCompanyAccount = account.companyAccount === true;
        return (
          <div class="flex flex-col gap-1">
            <div class="text-sm text-text-01">{account.name}</div>
            {restriction.cannotBeCompanyAccount && restriction.reason && <div class="text-xs text-danger">{restriction.reason}</div>}
            {isExistingCompanyAccount && (
              <div class="text-xs text-text-03">{t('Company accounts cannot be converted to property accounts')}</div>
            )}
          </div>
        );
      },
    },
    {
      title: t('Property'),
      render: (account) => {
        const currentType = accountTypeMap().get(account.id);
        const isChecked = currentType === 'property';
        const isExistingCompanyAccount = account.companyAccount === true;
        return (
          <Checkbox
            htmlType="radio"
            checked={isChecked}
            disabled={isExistingCompanyAccount}
            onChange={() => {
              handleAccountTypeChange(account.id, 'property');
            }}
            name={`bank-account-${account.id}`}
          />
        );
      },
    },
    {
      title: t('Company'),
      render: (account) => {
        const currentType = accountTypeMap().get(account.id);
        const isChecked = currentType === 'company';
        const restriction = getAccountRestriction(account.id);
        return (
          <Checkbox
            htmlType="radio"
            checked={isChecked}
            disabled={restriction.cannotBeCompanyAccount}
            onChange={() => {
              handleAccountTypeChange(account.id, 'company');
            }}
            name={`bank-account-${account.id}`}
          />
        );
      },
    },
  ]);

  return <Table columns={columns()} data={bankAccounts() ?? []} />;
};

export default BankAccountChoose;
